import map from 'lodash/map'

import {formatPrice} from '../utils'

import * as URLS from '../urls'

// ///////////////////////////////
// ///// GET ALL EVENTS //////////
// ///////////////////////////////

// this "normalizes" events.all data as what we get from server is in
//  a different format than events.current
const shimEvent = ({
  cancelled,
  date,
  eventType,
  hasHto,
  htoArrivalDate,
  htoOnly,
  id,
  metadata,
  name,
  numMembers,
  orderId,
  ownerId,
  ownerRoleName,
  status,
}) => ({
  cancelled,
  date: htoOnly ? htoArrivalDate : date,
  hasHto,
  id,
  metadata,
  name,
  numMembers,
  orderId,
  owner: ownerId,
  ownerRole: ownerRoleName,
  status,
  type: eventType,
})

export const shimEvents = (events) => map(events, shimEvent)

// ////////////////////////////////////
// ///// EVENT LOOKS AND PEOPLE ///////
// ////////////////////////////////////

const shimLooks = (looks) =>
  map(looks, (look) => ({
    availableArrivalDates: look.hto ? look.hto.availableArrivalDatesString.map((date) => date) : [], // Revise in BN-163
    hto: look.isHto,
    id: look.id,
    name: look.name,
    products: look.eventLookItems.map((item) => ({
      buyPrice: parseFloat(item.product.buyPrice),
      category: item.product.category,
      id: item.id,
      name: item.product.name,
      photos: item.product.photos[0],
      price: parseInt(formatPrice(item.product.price), 10),
      rentPrice: parseFloat(item.product.rentPrice),
      slug: item.product.slug,
      transactionType: item.transactionType,
      url: `${URLS.COLLECTION}/${item.product.category}/${item.product.urlSlug}`,
    })),
    visibleProductGroup: 1,
  }))

const shimPeople = (eventLooks, userId) => {
  const participantLooks = []
  const userLooks = []
  // if the event does not have looks yet, just return empty
  // otherwise, format looks by user and participant
  if (eventLooks) {
    eventLooks.map((look) => {
      const wearers = []
      let hasParticipants
      look.participants.map((participant) => {
        if (participant.id === userId) {
          // the user is wearing the look
          if (!look.isHto) {
            userLooks.push(look.id)
          }
        } else {
          // a participant is wearing the look
          hasParticipants = true
          const wearer = {
            email: participant.email,
            firstName: participant.firstName,
            lastName: participant.lastName,
            ownerPays: participant.paidFor,
            userEventLookId: participant.userEventLookId,
          }
          // aggregate the wearers' details
          wearers.push(wearer)
        }
        return participant
      })
      if (hasParticipants) {
        return participantLooks.push({
          lookId: look.id,
          wearers,
        })
      }
      return look
    })
  }
  return {
    participants: participantLooks,
    user: userLooks,
  }
}

export const shimGetEventLooksAndPeople = (userId, json) => ({
  looks: shimLooks(json.eventLooks),
  people: shimPeople(json.eventLooks, userId),
})

const currentEventData = (json) => ({
  currentEvent: {
    date: json.date,
    id: json.id,
    name: json.name,
    numMembers: json.numMembers,
    owner: json.ownerId,
    ownerRole: json.ownerRoleName,
    type: json.eventType,
  },
})

export const shimCurrentEventLooksAndPeople = (userId, json) => ({
  ...currentEventData(json),
  looksAndPeople: {...shimGetEventLooksAndPeople(userId, json)},
})

// used in addLook and addLookItem actions
export const shimLook = ({
  eventLookItems,
  hto,
  id,
  isHto,
  name,
  participants, // a list of participants (and their information) that are wearing this look
}) => {
  const look = {
    availableArrivalDates: isHto ? hto.availableArrivalDatesString : [],
    id,
    hto: isHto,
    name,
    participants,
    products: [],
    visibleProductGroup: 1,
  }

  for (let index = 0; index < eventLookItems.length; index += 1) {
    const {product} = eventLookItems[index]

    look.products.push({
      id: eventLookItems[index].id,
      name: product.name,
      slug: product.slug,
      category: product.category,
      url: `/products/${product.urlSlug}`,
      buyPrice: parseFloat(product.buyPrice),
      rentPrice: parseFloat(product.rentPrice),
      price: parseFloat(product.price),
      photos: product.photos[0],
      transactionType: eventLookItems[index].transactionType,
    })
  }
  return look
}

export const shimParticipantLooks = (looks) =>
  map(looks, (look) => ({
    id: look.id,
    hto: look.isHto,
    payer: look.payer,
    products: map(look.userEventLookItems, (item) => ({
      buyPrice: parseFloat(item.product.buyPrice),
      category: item.product.category,
      id: item.product.id,
      isPurchasable: item.product.isPurchasable,
      isRentable: item.product.isRentable,
      isSoldoutForBuy: item.product.isSoldoutForBuy,
      name: item.product.name,
      slug: item.product.slug,
      url: `/products/${item.product.urlSlug}`,
      rentPrice: parseFloat(item.product.rentPrice),
      photos: item.product.photos[0],
      transactionType: item.transactionType,
      userEventLookItemId: item.userEventLookItemId, // used for participant editing own look
    })),
  }))
